import { MAX_CHUNK_CHARS, type EventsPayload } from "./protocol";

const isHighSurrogate = (code: number) => code >= 0xd800 && code <= 0xdbff;

// Chunked-batch ids only need to be unique among one sender's in-flight
// batches; seq can restart after a reconnect, so add time and a counter.
let chunkedBatchCount = 0;

/**
 * Serializes one batch of rrweb events into socket payloads. Small batches go
 * as a single message; a large one (typically the full-page snapshot) is split
 * into slices of at most `maxChars`, never cutting a surrogate pair in half.
 */
export function encodeBatch(
  sessionId: string,
  seq: number,
  events: readonly unknown[],
  maxChars: number = MAX_CHUNK_CHARS
): EventsPayload[] {
  const data = JSON.stringify(events);
  if (data.length <= maxChars) return [{ session_id: sessionId, seq, data }];

  const slices: string[] = [];
  let start = 0;
  while (start < data.length) {
    let end = Math.min(start + maxChars, data.length);
    // Keep a surrogate pair together, but always make progress (maxChars of 1
    // would otherwise back off to an empty slice forever).
    if (
      end < data.length &&
      end - 1 > start &&
      isHighSurrogate(data.charCodeAt(end - 1))
    )
      end--;
    slices.push(data.slice(start, end));
    start = end;
  }

  chunkedBatchCount += 1;
  const id = `${seq}-${Date.now().toString(36)}-${chunkedBatchCount.toString(36)}`;
  return slices.map((slice, index) => ({
    session_id: sessionId,
    seq,
    chunk: { id, index, count: slices.length },
    data: slice
  }));
}

export interface Reassembler {
  /** Returns the batch's events once every slice has arrived, otherwise null. */
  push(payload: EventsPayload): unknown[] | null;
  reset(): void;
}

/**
 * Rebuilds batches split by `encodeBatch`. Keeps at most `maxPending`
 * incomplete batches; the oldest is dropped if a sender goes away mid-batch.
 */
export function createReassembler(maxPending = 8): Reassembler {
  const pending = new Map<
    string,
    { parts: Array<string | undefined>; received: number }
  >();

  return {
    push(payload) {
      if (!payload.chunk) return JSON.parse(payload.data) as unknown[];

      const { id, index, count } = payload.chunk;
      let entry = pending.get(id);
      if (!entry) {
        if (pending.size >= maxPending) {
          const oldest = pending.keys().next().value;
          if (oldest !== undefined) pending.delete(oldest);
        }
        entry = { parts: new Array<string | undefined>(count), received: 0 };
        pending.set(id, entry);
      }

      if (index < 0 || index >= count) return null;
      if (entry.parts[index] === undefined) {
        entry.parts[index] = payload.data;
        entry.received += 1;
      }
      if (entry.received < count) return null;

      pending.delete(id);
      return JSON.parse(entry.parts.join("")) as unknown[];
    },
    reset() {
      pending.clear();
    }
  };
}
